import { useMemo } from 'react';
import { useConfig, useGate } from 'statsig-react';
import { z } from 'zod';
import { ENVIRONMENT } from '@soundxyz/utils/const';
import { useAuth } from '@context/auth/base';
import { FEATURE_GATES } from '@utils/constants';

const postingAllowListConfigSchema = z.object({
  production: z.object({
    allowAll: z.boolean(),
    allowedUserIds: z.array(z.string()),
  }),
  preview: z.object({
    allowAll: z.boolean(),
    allowedUserIds: z.array(z.string()),
  }),
  staging: z.object({
    allowAll: z.boolean(),
    allowedUserIds: z.array(z.string()),
  }),
});

export function usePostsEnabled(): {
  viewingPostsEnabled: boolean;
  postingEnabled: boolean;
  showPostingDisabledTooltip: boolean;
} {
  const { authUser } = useAuth();
  const { value: activityFeedV2Enabled } = useGate(FEATURE_GATES.ACTIVITY_FEED_V2);
  const {
    config: { value },
  } = useConfig('posting_allowlist');

  const postingEnabled = useMemo(() => {
    const parseResponse = postingAllowListConfigSchema.safeParse(value);
    if (!parseResponse.success) {
      return false;
    }

    const postingAllowlistConfigValue = parseResponse.data;

    if (postingAllowlistConfigValue) {
      if (Object.keys(postingAllowlistConfigValue).includes(ENVIRONMENT)) {
        const envConfig =
          postingAllowlistConfigValue[ENVIRONMENT as keyof typeof postingAllowlistConfigValue];
        if (envConfig.allowAll) {
          return true;
        }
        if (authUser?.id && envConfig.allowedUserIds.includes(authUser.id)) {
          return true;
        }
      }
    }
    return false;
  }, [value, authUser?.id]);

  const showPostingDisabledTooltip = useMemo(
    () => Boolean(authUser?.id) && !postingEnabled,
    [authUser, postingEnabled],
  );

  return {
    viewingPostsEnabled: activityFeedV2Enabled,
    postingEnabled,
    showPostingDisabledTooltip,
  };
}
